import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { lookInsideImages } from '../data/lookInside';
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateState = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const slide = track.firstElementChild as HTMLElement | null;
    const step = slide ? slide.offsetWidth + 20 : track.clientWidth;
    setActive(Math.min(lookInsideImages.length - 1, Math.round(track.scrollLeft / step)));
    setCanPrev(track.scrollLeft > 4);
    setCanNext(track.scrollLeft + track.clientWidth < track.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateState();
    window.addEventListener('resize', updateState);
    return () => window.removeEventListener('resize', updateState);
  }, [updateState]);
  const scrollToIndex = (index: number) => {
    const track = trackRef.current;
    const slide = track?.children[index] as HTMLElement | undefined;
    if (!track || !slide) return;
    track.scrollTo({
      left: slide.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  };
  const scrollByDirection = (direction: 1 | -1) => {
    const track = trackRef.current;
    const slide = track?.firstElementChild as HTMLElement | null;
    if (!track || !slide) return;
    track.scrollBy({
      left: direction * (slide.offsetWidth + 20),
      behavior: 'smooth'
    });
  };
  return <section className="border-t border-electric/10 bg-navy py-16 sm:py-20" aria-labelledby="look-inside-title" data-id="element-91" data-mp="12hrr701z5ir">
      <div className="mx-auto max-w-6xl px-4" data-id="element-92" data-mp="3sfm9vd3hnmif">
        <div className="mx-auto max-w-3xl text-center" data-id="element-93" data-mp="3seuea29w5os5">
          <p className="text-xs font-bold uppercase tracking-[.16em] text-electric" data-id="element-94" data-mp="34prykzyti60a">Look Inside</p>
          <h2 id="look-inside-title" className="mt-3 text-balance text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl" data-id="element-95" data-mp="2zr6x6x7txga9">
            Discover What's Inside <span className="text-gold" data-id="element-96" data-mp="18mn8bnng1vru">ELECTRICAL ENGINEERING TEACHER READY</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-97" data-mp="1dfpv9cxpu6z2">
            <span className="h-1 w-10 rounded-full bg-electric" data-id="element-98" data-mp="1hf6ydzpo6s12" />
            <span className="h-1 w-3 rounded-full bg-gold" data-id="element-99" data-mp="kpzesx8m2szs" />
          </span>
          <p className="mt-6 leading-relaxed text-ink" data-id="element-100" data-mp="ks2e67vnlyh6">
            Electrical engineering fundamentals, DC and AC circuits, circuit analysis, electrical machines,
            electromagnetism, transformers, motors, power generation, transmission and distribution, three-phase systems,
            electrical installations, protection systems, industrial control, automation, teaching methodologies, 150
            classroom activities, technical assessments, 100 real-world engineering case studies, 200 essential electrical
            terms, and structured lesson planning resources.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-101" data-mp="2f9rgaw0ow0tr">
          <ul ref={trackRef} onScroll={updateState} className="flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-102" data-mp="2aisvbj4raek6">
            {lookInsideImages.map((image, index) => <li key={image.src} className="w-[82%] shrink-0 snap-start sm:w-[calc((100%-20px)/2)] lg:w-[calc((100%-40px)/3)]" aria-roledescription="slide" aria-label={`${index + 1} of ${lookInsideImages.length}`} data-id="element-103" data-mp="1wj9u7aad43hd" data-mpi={index}>
                <figure className="overflow-hidden rounded-2xl border border-electric/25 bg-panel p-2 shadow-lg shadow-black/30" data-id="element-104" data-mp="3ly7rwlt1z4er">
                  <img src={image.src} alt={image.alt} loading="lazy" className="aspect-[4/5] w-full rounded-xl bg-white object-cover" data-id="element-105" data-mp="3hwcrdjiy4v21" />
                </figure>
              </li>)}
          </ul>

          <button type="button" onClick={() => scrollByDirection(-1)} disabled={!canPrev} aria-label="Previous image" className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-electric/50 bg-navy/90 text-white shadow-lg shadow-black/40 transition-[opacity,background-color] duration-150 ease-out hover:bg-panel focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-0 lg:-left-5" data-id="element-106" data-mp="3fy33ocqbpvbz">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="2n9xgljbsru8z" />
          </button>
          <button type="button" onClick={() => scrollByDirection(1)} disabled={!canNext} aria-label="Next image" className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-electric/50 bg-navy/90 text-white shadow-lg shadow-black/40 transition-[opacity,background-color] duration-150 ease-out hover:bg-panel focus:outline-none focus-visible:ring-4 focus-visible:ring-gold/50 disabled:pointer-events-none disabled:opacity-0 lg:-right-5" data-id="element-108" data-mp="2s9aecz47ilqc">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-109" data-mp="2rkjhj5ewygjm" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-110" data-mp="2mlf8qfvwiktt">
          {lookInsideImages.map((image, index) => <button key={image.src} type="button" onClick={() => scrollToIndex(index)} aria-label={`Go to image ${index + 1}`} aria-current={active === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold ${active === index ? 'w-7 bg-gold' : 'w-2.5 bg-electric/40 hover:bg-electric/70'}`} data-id="element-111" data-mp="2wdclvs6gzmv6" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}